import {
    AssessmentSchedule as APIAssessmentSchedule,
    Assessment,
    CreateDimensionInput,
    Dimension,
    Issue,
    LinkRegistry,
    Location,
    MeasurementInput,
    Property,
    QuickQuote,
    ResourceAssessmentScheduleAssociation,
    ResourceNotificationSetting,
    SubmitQuickQuoteInput,
    Tag,
    UpdateDimensionInput
} from "../../../API";
import {
    AssessmentSchedule,
    Contact,
} from "../../../models";
import React, {
    Dispatch,
    FC,
    SetStateAction,
    memo
} from "react";

import { AssessmentsContextProvider } from "../contexts/AssessmentsContextProvider";
import { ContactContextProvider } from "../contexts/ContactContextProvider";
import { CurrentUserContextProvider } from "../contexts/CurrentUserContext";
import { DashboardFilterProvider } from "../DashboardFilterContext";
import { ImageEntity } from "../../../lib/dashboard/types/ImageEntity";
import { IssueFilterContextProvider } from "../contexts/IssueFilterContext";
import { IssuesContextProvider } from "../contexts/IssuesContextProvider";
import { LocationsContextProvider } from "../contexts/LocationContextProvider";
import { PageNavigationContextProvider } from "../contexts/PageNavigationContextProvider";
import { PropertyPage } from "./PropertyPage";
import { ResourceNotificationSettingDraft } from "../../../lib/dashboard/types/ResourceNotificationSettingDraft";
import { ResourceNotificationSettingsContextProvider } from "../contexts/ResourceNotificationSettingContextProvider";
import { SnackbarProvider } from "notistack";
import { TagsContextProvider } from "../contexts/TagsContextProvider";
import { ThemeProvider } from "@emotion/react";
import { theme } from "../../../assets/style/dashboardTheme";

export type TeneraPropertyDashboardProps = {
    property: Property;
    locations: Array<Location>;
    linkRegistries: Array<LinkRegistry>;
    issues: Array<Issue>;
    images: Array<ImageEntity>;
    assessments: Array<Assessment>;
    assessmentSchedules: Array<APIAssessmentSchedule>;
    resourceAssessmentScheduleAssociations: Array<ResourceAssessmentScheduleAssociation>;
    currentUserId?: string;
    submitIssue?: (locationId: string, description: string, images?: File[], measurement?: MeasurementInput) => (Promise<void>);
    createLocation?: (locationName: string, locationImages?: Blob[]) => (void | Promise<void>);
    editLocation?: (locationId: string, locationName: string) => (void | Promise<void>);
    deleteLocation?: (locationId: string) => (void | Promise<void>);
    fetchAssessmentScheduleStatus?: "idle" | "error" | "loading" | "success";
    missedAssessmentNotificationSettings?: Array<ResourceNotificationSetting>;
    issueSubmissionNotificationSetting?: ResourceNotificationSetting;
    updateIssueSubmissionNotificationSetting?: (resourceNotificationSetting: ResourceNotificationSettingDraft) => (void | Promise<void>);
    createAssessmentSchedule?: (schedule: AssessmentSchedule, selectedLocationIds: Array<string>, missedAssessmentNotificationSetting: ResourceNotificationSettingDraft) => Promise<void>;
    onEditScheduleSubmit?: (schedule: AssessmentSchedule, selectedLocationIds: Array<string>, missedAssessmentNotificationSetting: ResourceNotificationSettingDraft) => Promise<void>;
    onDeleteAssessmentSchedule?: (assessmentSchedule: AssessmentSchedule, associatedLocations: Location[]) => Promise<void>;
    onEditIssueSubmit?: (id: string, issue: Issue) => Promise<void>;
    onEditAssessmentSubmit?: (assessmentId: string, assessment: Assessment) => Promise<void>;
    contacts?: Array<Contact>;
    setIsOpenCreateContactDialog?: Dispatch<SetStateAction<boolean>>;
    tags: Array<Tag>;
    createTag?: (tagName: string) => Promise<Tag>;
    submitQuickQuotes?: (quickQuotes: SubmitQuickQuoteInput[]) => Promise<void>
    dimensions?: Dimension[];
    onEditDimensionSubmit?: (dimension: UpdateDimensionInput) => Promise<Dimension>;
    onCreateDimension?: (dimension: CreateDimensionInput) => Promise<Dimension>;
    quickQuotes?: QuickQuote[];
    issueIdToProposalItemPriceTotalMap?: Map<string, number>;
};

export const TeneraPropertyDashboard: FC<TeneraPropertyDashboardProps> = memo<TeneraPropertyDashboardProps>((props: TeneraPropertyDashboardProps) => {
    const {
        property,
        issues,
        locations,
        linkRegistries,
        images,
        assessmentSchedules,
        assessments,
        resourceAssessmentScheduleAssociations,
        submitIssue,
        createLocation,
        editLocation,
        deleteLocation,
        fetchAssessmentScheduleStatus,
        createAssessmentSchedule,
        onEditScheduleSubmit,
        onDeleteAssessmentSchedule,
        onEditIssueSubmit,
        contacts,
        setIsOpenCreateContactDialog,
        missedAssessmentNotificationSettings,
        updateIssueSubmissionNotificationSetting,
        issueSubmissionNotificationSetting,
        tags,
        createTag,
        submitQuickQuotes,
        onEditAssessmentSubmit,
        dimensions,
        onEditDimensionSubmit,
        onCreateDimension,
        quickQuotes,
        issueIdToProposalItemPriceTotalMap
    } = props;

    const assessmentsContextValue = {
        assessments,
        assessmentSchedules,
        resourceAssessmentScheduleAssociations,
        onDeleteAssessmentSchedule,
        createAssessmentSchedule,
        onEditScheduleSubmit,
        fetchAssessmentScheduleStatus,
        onEditAssessmentSubmit
    };

    const locationContextValue = {
        locations,
        createLocation,
        editLocation,
        deleteLocation
    };

    const issuesContextValue = {
        issues,
        submitIssue,
        onEditIssueSubmit,
        images,
        linkRegistries,
        submitQuickQuotes,
        dimensions,
        onEditDimensionSubmit,
        onCreateDimension,
        quickQuotes,
        issueIdToProposalItemPriceTotalMap
    };

    const resourceNotificationSettingContextValue = {
        missedAssessmentNotificationSettings,
        issueSubmissionNotificationSetting,
        updateIssueSubmissionNotificationSetting
    };

    return (
        <PageNavigationContextProvider>
            <IssueFilterContextProvider>
                <DashboardFilterProvider>
                    <ThemeProvider theme={theme}>
                        <SnackbarProvider>
                            <ContactContextProvider contacts={contacts} setIsOpenCreateContactDialog={setIsOpenCreateContactDialog}>
                                <IssuesContextProvider value={issuesContextValue}>
                                    <LocationsContextProvider value={locationContextValue}>
                                        <AssessmentsContextProvider value={assessmentsContextValue}>
                                            <ResourceNotificationSettingsContextProvider value={resourceNotificationSettingContextValue}>
                                                <TagsContextProvider value={{ tags, createTag }}>
                                                    <CurrentUserContextProvider value={{ userId: props.currentUserId }}>
                                                        <PropertyPage property={property} />
                                                    </CurrentUserContextProvider>
                                                </TagsContextProvider>
                                            </ResourceNotificationSettingsContextProvider>
                                        </AssessmentsContextProvider>
                                    </LocationsContextProvider>
                                </IssuesContextProvider>
                            </ContactContextProvider>
                        </SnackbarProvider>
                    </ThemeProvider>
                </DashboardFilterProvider>
            </IssueFilterContextProvider>
        </PageNavigationContextProvider>
    );
});